import { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { adminLLM, type LLMAnalysis } from '../../api/admin';
import { Brain, Send, Sparkles, Clock, Loader2, RefreshCw } from 'lucide-react';

const QUICK_PROMPTS = [
  'Give me a comprehensive business analysis',
  'What products should I promote this week?',
  'How can I improve customer retention?',
  'Analyze brand performance and suggest inventory changes',
  'What pricing strategies should I consider?',
  'Identify growth opportunities for next month',
];

export default function AIInsightsPage() {
  const { isDark } = useTheme();
  const [prompt, setPrompt] = useState('');
  const [analysis, setAnalysis] = useState<LLMAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState<{ prompt: string; analysis: LLMAnalysis }[]>([]);

  const runAnalysis = async (customPrompt?: string) => {
    const p = customPrompt ?? prompt;
    setLoading(true);
    setError('');
    try {
      const result = await adminLLM.analyze(p || undefined);
      setAnalysis(result);
      setHistory(prev => [{ prompt: p || 'General analysis', analysis: result }, ...prev.slice(0, 9)]);
      setPrompt('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Brain size={24} className="text-purple-500" />
          AI Insights
        </h1>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          AI-powered business analysis using Ollama LLM
        </p>
      </div>

      {/* Prompt input */}
      <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-purple-500" />
          <span className="text-sm font-medium">Ask AI</span>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="Ask about your business performance, products, customers..."
            onKeyDown={e => { if (e.key === 'Enter' && !loading) runAnalysis(); }}
            className={`flex-1 px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors
              ${isDark
                ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-purple-500'
                : 'bg-gray-50 border-gray-200 placeholder-gray-400 focus:border-purple-500'
              }`}
          />
          <button
            onClick={() => runAnalysis()}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {loading ? 'Analyzing...' : 'Analyze'}
          </button>
        </div>

        {/* Quick prompts */}
        <div className="flex flex-wrap gap-2 mt-3">
          {QUICK_PROMPTS.map(q => (
            <button
              key={q}
              onClick={() => runAnalysis(q)}
              disabled={loading}
              className={`px-3 py-1 rounded-full text-xs transition-colors border
                ${isDark
                  ? 'border-gray-700 text-gray-400 hover:text-purple-400 hover:border-purple-500/30'
                  : 'border-gray-200 text-gray-500 hover:text-purple-500 hover:border-purple-300'
                } disabled:opacity-50`}
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-xl border border-red-500/20 text-sm">
          {error}
        </div>
      )}

      {/* Analysis result */}
      {analysis && (
        <div className={`rounded-xl border p-6 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold flex items-center gap-2">
              <Brain size={18} className="text-purple-500" />
              AI Analysis
            </h2>
            <div className="flex items-center gap-2">
              <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <Clock size={12} />
                {new Date(analysis.generated_at).toLocaleTimeString('en-IN')}
              </span>
              <button
                onClick={() => runAnalysis()}
                disabled={loading}
                className="p-1.5 rounded-lg text-purple-400 hover:bg-purple-500/10 transition-colors"
                title="Re-analyze"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* Render markdown-ish analysis */}
          <div className={`prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''}`}>
            {analysis.analysis.split('\n').map((line, i) => {
              if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold mt-4 mb-2">{line.replace('## ', '')}</h2>;
              if (line.startsWith('### ')) return <h3 key={i} className="text-base font-semibold mt-3 mb-1">{line.replace('### ', '')}</h3>;
              if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-bold mt-2">{line.replace(/\*\*/g, '')}</p>;
              if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className={`ml-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{line.replace(/^[-*]\s/, '')}</li>;
              if (line.startsWith('> ')) return <blockquote key={i} className={`border-l-2 border-purple-500 pl-3 italic text-sm my-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{line.replace('> ', '')}</blockquote>;
              if (line.startsWith('```')) return null;
              if (line.trim() === '') return <br key={i} />;
              return <p key={i} className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{line}</p>;
            })}
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 1 && (
        <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <h2 className="font-semibold mb-3 text-sm">Previous Analyses</h2>
          <div className="space-y-2">
            {history.slice(1).map((h, i) => (
              <button
                key={i}
                onClick={() => setAnalysis(h.analysis)}
                className={`w-full text-left p-3 rounded-lg text-sm transition-colors
                  ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
              >
                <p className="font-medium truncate">{h.prompt}</p>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {new Date(h.analysis.generated_at).toLocaleString('en-IN')}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Info box */}
      {!analysis && !loading && (
        <div className={`rounded-xl border border-dashed p-8 text-center ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
          <Brain size={48} className={`mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <h3 className={`font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>AI-Powered Business Insights</h3>
          <p className={`text-sm max-w-md mx-auto ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Ask a question or click a quick prompt above to get detailed AI analysis of your business data. 
            Requires Ollama running locally with llama3.2 model. Falls back to rule-based analysis if unavailable.
          </p>
        </div>
      )}
    </div>
  );
}
